import React, { useState } from 'react'
import { usePermissions } from '@plug-core/state'
import useWallet from '@plug-core/hooks/useWallet'
import {
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { TfiUnlink } from 'react-icons/tfi'
import Prompt from '@components/Prompt'
import Button from '@components/Button'
import EmptyList from '@components/EmptyList'
import { useTranslation } from 'react-i18next'
import { initializeMessenger } from '@core/messengers'

import type { Permission } from '@plug-core/interfaces/permissions'

const messenger = initializeMessenger({ connect: 'background' })

const ConnectedApps = () => {
  const { permissions } = usePermissions()
  const { wallet } = useWallet()
  const { palette } = useTheme()
  const [permissionInEdit, setPermissionInEdit] = useState<Permission>()
  const { t } = useTranslation()

  const principalPermissions = Object.values(permissions[wallet.principalId] || {})

  if (principalPermissions.length === 0)
    return (
      <EmptyList
        title={t('connectedApps.emptyTitle')}
        description={t('connectedApps.emptyDescription')}
      />
    )

  const terminateSession = () => {
    messenger.send('session-terminate', {
      principal: wallet.principalId,
      host: permissionInEdit!.host,
    })

    setPermissionInEdit(undefined)
  }

  return (
    <Stack flex={1}>
      <Stack component={List} mx={2}>
        {Object.values(principalPermissions).map((permission, index) => (
          <ListItem key={index}>
            <ListItemAvatar>
              <Avatar src={permission.logo} />
            </ListItemAvatar>
            <ListItemText primary={permission.name} secondary={permission.host} />
            <ListItemSecondaryAction>
              <IconButton color="secondary" onClick={() => setPermissionInEdit(permission)}>
                <TfiUnlink color={palette.error.main} />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </Stack>
      <Prompt show={!!permissionInEdit} close={() => setPermissionInEdit(undefined)}>
        <Stack spacing={2}>
          <Stack>
            <Typography textAlign="center" variant="h6">
              Close dApp session
            </Typography>
            <Typography textAlign="center" variant="caption">
              Are you sure you want to disconnect from {permissionInEdit?.name}
            </Typography>
          </Stack>
          <Stack>
            <Button variant="destroy" onClick={terminateSession} fullWidth>
              Disconnect
            </Button>
            <Button onClick={() => setPermissionInEdit(undefined)} fullWidth>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Prompt>
    </Stack>
  )
}

export default ConnectedApps
